import React from 'react';

import { Button } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { Cell } from 'react-table';

import { DeleteIcon } from 'assets/icons';
import { SeverityIcon, Table } from 'components/UI';
import { getSeveritySvg } from 'utils/Icons';
import { SmartEventActionType } from 'constants/enums';

interface ISmartEventActionsTableProps {
  actions: ISmartEventActionForm[];
  setActions?: any;
}

const SmartEventActionsTable = ({ actions, setActions }: ISmartEventActionsTableProps) => {
  const intl = useIntl();

  const getAlertTypeString = (alertType: string) => {
    switch (alertType) {
      case 'compliance':
        return 'medicalComplianceKey';
      case 'medical':
      default:
        return 'medicalLabelKey';
    }
  };

  const actionColumns: any[] = [
    {
      id: 'type',
      Header: intl.formatMessage({ id: 'typeKey' }),
      Cell: ({ row: { original } }: Cell<ISmartEventActionForm>) => {
        switch (original.actionType) {
          case SmartEventActionType.ALERT:
            return (
              intl.formatMessage({ id: 'alertForClinicianKey' }) +
              ' (' +
              intl.formatMessage({ id: getAlertTypeString(original.alertType) }) +
              ')'
            );
          case SmartEventActionType.MEASUREMENT:
            return intl.formatMessage({ id: 'measurementTaskKey' });
          case SmartEventActionType.QUESTIONNAIRE:
            return intl.formatMessage({ id: 'questionnaireTaskKey' });
          case SmartEventActionType.MESSAGE:
            return intl.formatMessage({ id: 'messageToPatientKey' });
        }
      }
    },
    {
      id: 'properties',
      Header: intl.formatMessage({ id: 'propertiesKey' }),
      Cell: ({ row: { original } }: Cell<ISmartEventActionForm>) => {
        switch (original.actionType) {
          case SmartEventActionType.ALERT:
            return (
              <>
                <div className="d-flex align-items-center">
                  <SeverityIcon className="me-2" name={getSeveritySvg(original.alertLevel, '')} />
                  <p className="m-0">{original.alertTitle}</p>
                </div>
                <hr />
                <p className="m-0 word-break-word whitespace-prewrap">
                  {original.alertDescription}
                </p>
              </>
            );
          case SmartEventActionType.MEASUREMENT:
            return intl.formatMessage({ id: original.measurementClassAction });
          case SmartEventActionType.QUESTIONNAIRE:
            return intl.formatMessage({ id: original.questionnaireAction });
          case SmartEventActionType.MESSAGE:
            return (
              <p className="m-0 word-break-word whitespace-prewrap">{original.messageAction}</p>
            );
        }
      }
    }
  ];

  if (setActions) {
    actionColumns.push({
      Header: ' ',
      Cell: ({ row: { original } }: Cell<ISmartEventConditionForm>) => {
        return (
          <Button
            variant="link"
            className="d-flex align-items-center"
            onClick={() => original.id && deleteAction(original.id)}
          >
            <DeleteIcon className="icon pe-2" />
            <p className="m-0">{intl.formatMessage({ id: 'deleteKey' })}</p>
          </Button>
        );
      }
    });
  }

  const deleteAction = (id: string) => {
    setActions(actions.filter((action) => action.id !== id));
  };

  return (
    <Table
      extraClassNames="pb-5"
      dataTestIdPrefix="actions"
      columns={actionColumns}
      data={actions}
      noDataTxt={intl.formatMessage({ id: 'noDataKey' })}
      hideMargin
      autoHeight
    />
  );
};

export default SmartEventActionsTable;
